<template>
  <main :class="{ dark: isElevated }">
    <div data-scroll-el="pdpContainer" />
    <div class="dark:bg-black dark:c-white">
      <div>
        <slot name="notifications" />
        <transition name="fade">
          <div v-if="productPending && !product">
            <slot name="skeletons" />
          </div>
          <div v-else-if="product">
            <section v-if="isElevated" class="relative" :style="`height: calc(100svh - ${pxToRem(header.height.promoBar)})`">
              <base-picture
                class="full"
                fit="cover"
                :props-img="{ class: 'h-full' }"
                :src="getImageTransformations(product.elevatedPDPDetails?.imageURL)"
              />
              <div v-style:top="{ sm: '4.25rem', lg: '6.75rem' }" class="absolute w-full">
                <div class="container">
                  <vf-breadcrumbs
                    class="w-fit rounded-full bg-black/60 px-4 py-2 [&_a:hover]:c-$ch children:c-white !children:overflow-visible"
                    :items="product.breadcrumbs || []"
                    :style="{ '--ch': colors.grey['50'] }"
                  />
                </div>
              </div>
              <div
                v-style:p="{ sm: '7.5rem 1rem 6rem', lg: '7.5rem 1rem 4.5rem' }"
                class="dark absolute bottom-0 w-full lg:text-center"
                style="background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.8))"
              >
                <div v-style:max-w="{ lg: '50rem' }" class="relative mx-a dark:c-white">
                  <div v-if="product && productPrice">
                    <h1 class="elevate-title-2 fw-bold" data-test-id="product-name">
                      {{ product.name }}
                    </h1>
                    <product-reviews-summary class="mt-2" :reviews-data="reviewsData" />
                    <product-pricing
                      class="elevate-body-3 mt-4 fw-medium"
                      :currency="product.currency"
                      :price="productPrice"
                      :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
                      :show-original-price-for-omnibus="$feature.showOmnibusBadgeOnQuickshopAndPDP"
                    />
                  </div>
                  <div class="absolute bottom-0 w-full flex justify-end lg:top-full lg:mt-2 lg:justify-center">
                    <base-button :aria-label="$t.scrollDown" @click="scrollToElement('details-section')">
                      <vf-icon
                        class="c-white"
                        name="arrow"
                        size="lg"
                        style="animation: arrow-animation 1.5s linear infinite"
                      />
                    </base-button>
                  </div>
                </div>
              </div>
            </section>
            <section
              v-if="isElevated"
              v-style:max-w="{ lg: '50rem' }"
              class="pb-10 pt-4 container lg:pt-10 lg:text-center"
            >
              <p class="elevate-body-1">
                {{ product?.elevatedPDPDetails?.headlineText }}
              </p>
            </section>
            <vf-breadcrumbs
              v-if="product && !isElevated"
              class="my-2 text-sm container lg:my-4"
              :items="product.breadcrumbs || []"
              :last-step="{ label: product.name }"
            />
            <div class="container lg:grid lg:cols-12 lg:mb-10 lg:items-start lg:gap-8">
              <section class="lg:col-span-7 <lg:-mx-4">
                <product-badge
                  v-if="product.badge"
                  v-bind="product.badge"
                  class="b-x b-t b-grey-80 bg-white c-grey-10"
                />
                <slot name="gallery" />
              </section>
              <section class="lg:col-span-5" data-scroll-el="details-section">
                <slot name="heading" />
                <slot name="variant-selection" />
                <slot name="size-and-fit" />
                <slot name="upsells" />
                <slot name="espot-above-add-to-cart" />
                <slot name="klarna-payments" />
                <slot name="ctas" />
                <slot name="shipping-methods" />
                <div>
                  <slot name="espot-above-accordions" />
                </div>
                <slot v-if="!showElevatedDetails" name="accordions" />
                <slot name="espot-below-accordions" />
              </section>
            </div>
            <product-accordions-elevate
              v-if="product.details && showElevatedDetails"
              :description="product.elevatedPDPDetails?.byLineCopy2"
              :heading="product.elevatedPDPDetails?.byLineCopy1"
              :product-details="product.details"
            />
            <slot name="espot-lazy-below-gallery" />
            <slot name="reviews" />
          </div>
        </transition>
        <slot name="recommendations" />
      </div>
      <slot name="espot-lazy-above-footer" />
    </div>
    <client-only>
      <teleport to="body">
        <slot name="body" />
      </teleport>
    </client-only>
    <slot />
  </main>
</template>

<script setup lang="ts">
defineSlots<{
  'accordions': void
  'body': void
  'ctas': void
  'espot-above-accordions': void
  'espot-above-add-to-cart': void
  'espot-below-accordions': void
  'espot-lazy-above-footer': void
  'espot-lazy-below-gallery': void
  'gallery': void
  'heading': void
  'klarna-payments': void
  'notifications': void
  'recommendations': void
  'reviews': void
  'shipping-methods': void
  'size-and-fit': void
  'skeletons': void
  'upsells': void
  'variant-selection': void
}>()

const { colors } = useAppConfig().ds
const header = useHeaderStore()
const { $t } = useNuxtApp()
const { product, productPrice, productPending, reviewsData } = usePdp()

const isElevated = computed(() => product.value?.elevatedPDPDetails?.enable || false)
const showElevatedDetails = computed(() =>
  product.value?.elevatedPDPDetails?.byLineCopy1 && product.value?.elevatedPDPDetails?.byLineCopy2)
</script>
